/**
 * `useVisitBilling` — the one read and the one write every billing surface shares.
 *
 * Read: `visitBillingGet` — the Claim's lines with their diagnoses, the merged billing
 * suggestion (the Claim decides the codes, the note's extension supplies the AI's
 * alternatives and rationale), the rendering provider and the patient. The charge quote
 * prices those codes for that provider (PD-1597), plus any code the editor has picked but
 * not saved yet, so a credential that cannot bill it is told so before Save (PD-1647).
 *
 * Write: `visitUpdateBilling` — the server rewrites the Claim and the note's
 * billing-suggestion extension at any Claim status, Submitted included (PD-1689). After a
 * save both the TanStack cache and the Medplum SDK cache are cleared for everything the
 * PATCH rewrites: the chart's Claim summary reads through the SDK cache, which TanStack
 * invalidation does not reach.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { useClient, useOrpc, type OrpcClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import { useSubmittedChargeQuote } from '@/pages/Patient/components/ProgressNoteEdit/useSubmittedChargeQuote'
import type {
  BillingDiagnosisSupport,
  BillingEntry as FormBillingEntry,
  BillingSuggestion as FormBillingSuggestion,
} from '@/pages/Patient/components/ProgressNoteEdit/progressNoteEditSchema'
import type { BillingEntry } from './BillingEditModal'

export type VisitBilling = Awaited<ReturnType<OrpcClient['visitBillingGet']>>
export type VisitBillingSuggestion = NonNullable<VisitBilling['billingSuggestion']>
type UpdateVisitBillingInput = Parameters<OrpcClient['visitUpdateBilling']>[0]

/**
 * The Claim's lines as the editor and the note form hold them (one id per row). An
 * `unavailable` row (PD-1958) is kept only for the billing editor, which blocks Save until
 * it is removed or replaced; the note form autosaves and would write it as an empty diagnosis.
 */
export function toBillingEntries(
  entries: VisitBilling['entries'],
  { keepUnavailable = false }: { keepUnavailable?: boolean } = {},
): BillingEntry[] {
  return entries.map((entry) => ({
    id: crypto.randomUUID(),
    procedure: entry.procedure,
    price: entry.price,
    quantity: entry.quantity,
    billingItems: entry.billingItems
      .filter((billingItem) => keepUnavailable || !billingItem.unavailable)
      .map((billingItem) => ({
        id: crypto.randomUUID(),
        dx: billingItem.dx,
        ...(billingItem.unavailable ? { unavailable: true } : {}),
      })),
    modifiers: entry.modifiers,
  }))
}

/** Whether the read left out a diagnosis it could not load (PD-1958): no copy of it may be saved. */
export function hasUnavailableDiagnoses(visitBilling: VisitBilling | undefined): boolean {
  return (
    Boolean(visitBilling?.diagnosesUnavailable) ||
    (visitBilling?.entries ?? []).some((entry) =>
      entry.billingItems.some((billingItem) => billingItem.unavailable),
    )
  )
}

/**
 * PD-2018 — the AI diagnosis codes still on a billing line whose review nobody has
 * confirmed, once per code. A code replaced on every line leaves its entry behind; it
 * no longer counts.
 */
export function diagnosesNeedingReview(
  entries: BillingEntry[],
  aiDiagnoses: BillingDiagnosisSupport[] | undefined,
): string[] {
  const linked = new Set(entries.flatMap((e) => e.billingItems.map((b) => b.dx?.code)))
  return (aiDiagnoses ?? [])
    .filter((dx) => dx.review && !dx.confirmedAt && linked.has(dx.code))
    .map((dx) => dx.code)
}

/**
 * The server's suggestion in the note form's shape. Total Time rows are not part of it —
 * they live on the note's draft blob (PD-1321), and the form overlays them itself.
 */
export function toFormBillingSuggestion(
  suggestion: VisitBilling['billingSuggestion'] | undefined,
): FormBillingSuggestion | undefined {
  if (!suggestion) return undefined

  return {
    candidates: suggestion.candidates,
    selectedRank: suggestion.selectedRank,
    addOnCodes: suggestion.addOnCodes,
    modifiers: suggestion.modifiers,
    placeOfService: suggestion.placeOfService,
    durationMinutes: suggestion.durationMinutes,
    reasoning: suggestion.reasoning,
    providerTimeEntries: [],
    careCoordinatorTimeEntries: [],
    ...(suggestion.diagnoses ? { diagnoses: suggestion.diagnoses } : {}),
  }
}

/**
 * Drop one of the AI's candidates. The remaining ones are re-ranked 1..N so the
 * rank-based selection lookup stays deterministic, and `selectedRank` follows the
 * selected code so the highlighted chip stays attached to it.
 */
export function removeCandidate(
  suggestion: VisitBillingSuggestion,
  rank: number,
): VisitBillingSuggestion {
  const remaining = suggestion.candidates.filter((candidate) => candidate.rank !== rank)
  if (remaining.length === suggestion.candidates.length) return suggestion

  const selectedCode = suggestion.candidates.find((c) => c.rank === suggestion.selectedRank)?.code
  const reRanked = [...remaining]
    .sort((a, b) => a.rank - b.rank)
    .map((candidate, index) => ({ ...candidate, rank: index + 1 }))

  return {
    ...suggestion,
    candidates: reRanked,
    selectedRank: reRanked.find((c) => c.code === selectedCode)?.rank ?? reRanked[0]?.rank ?? 1,
  }
}

export function useVisitBilling(encounterId: string, pickedCodes: string[] = []) {
  const orpc = useOrpc()
  const client = useClient()
  const queryClient = useQueryClient()
  const medplum = useMedplum()

  const query = useQuery(orpc.visitBillingGet.queryOptions({ input: { encounterId } }))
  const data = query.data

  const entries = useMemo(
    () => toBillingEntries(data?.entries ?? [], { keepUnavailable: true }),
    [data],
  )
  const suggestion = data?.billingSuggestion ?? undefined

  // Every code the visit can bill — the lines, the AI's candidates and add-ons, plus what
  // the editor has picked since it opened — so a chip switch or a modal pick finds its
  // quote instead of the catalog fallback.
  const codes = useMemo(
    () =>
      [
        ...entries.map((entry) => entry.procedure?.code),
        ...(suggestion?.candidates ?? []).map((candidate) => candidate.code),
        ...(suggestion?.addOnCodes ?? []).map((addOn) => addOn.code),
        ...pickedCodes,
      ].filter((code): code is string => Boolean(code)),
    [entries, suggestion, pickedCodes],
  )
  const quote = useSubmittedChargeQuote({
    codes,
    practitionerId: data?.renderingProviderId ?? undefined,
    patientId: data?.patientId,
    encounterId,
  })

  const mutation = useMutation({
    mutationFn: (input: UpdateVisitBillingInput) => client.visitUpdateBilling(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.visitBillingGet.key() }),
        queryClient.invalidateQueries({ queryKey: ['DocumentReference'] }),
        queryClient.invalidateQueries({ queryKey: ['Claim'] }),
        queryClient.invalidateQueries({ queryKey: ['Provenance'] }),
        queryClient.invalidateQueries({ queryKey: ['Encounter'] }),
      ])
      medplum.invalidateSearches('Claim')
      medplum.invalidateSearches('DocumentReference')
      medplum.invalidateSearches('Provenance')
      medplum.invalidateSearches('Encounter')

      showNotification({
        color: 'green',
        title: 'Billing updated',
        message: 'Claim and progress note billing refreshed.',
      })
    },
    onError: (err) => {
      logError('billing', 'Update visit billing failed', err)
      const status = (err as { status?: number; statusCode?: number })?.status
      const isRejected = status === 400 || (err as { statusCode?: number })?.statusCode === 400
      showNotification({
        color: 'red',
        title: isRejected ? 'Billing not saved' : 'Update failed',
        message:
          isRejected && err instanceof Error && err.message
            ? err.message
            : 'Could not update billing. Please try again.',
      })
    },
  })
  const { mutateAsync } = mutation

  /** Writes the lines and the suggestion; rejects after the error notification is shown. */
  const save = useCallback(
    async (input: {
      entries: BillingEntry[] | FormBillingEntry[]
      suggestion: VisitBillingSuggestion | undefined
    }) => {
      await mutateAsync({
        encounterId,
        billing: input.entries,
        ...(input.suggestion ? { billingSuggestion: input.suggestion } : {}),
      })
    },
    [mutateAsync, encounterId],
  )

  return {
    data,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    entries,
    suggestion,
    quote,
    save,
    isSaving: mutation.isPending,
  }
}
